import * as React from 'react'
import CodeMirror, {
  ReactCodeMirrorProps,
  EditorView,
} from '@uiw/react-codemirror'
import { autocompletion, Completion } from '@codemirror/autocomplete'

export type CodeMirrorWithVariablesProps = ReactCodeMirrorProps & {
  disabled?: boolean
  hideInfo?: boolean
  getVariables: (
    segments: string[],
    opts: {
      isExplicit: boolean
      isOutsideKeys: boolean
    }
  ) => Array<Completion>
}

const themeExtension = EditorView.baseTheme({
  '.cm-tooltip.cm-tooltip-autocomplete': {
    position: 'absolute !important',
    transform: 'none !important',
    maxWidth: '90vw',
    maxHeight: '50vh',
    // left: 'unset !important',
  },
})

export const CodeMirrorWithVariables = ({
  disabled,
  getVariables,
  hideInfo,
  ...rest
}: CodeMirrorWithVariablesProps) => {
  const getVariablesRef = React.useRef(getVariables)
  // eslint-disable-next-line react-compiler/react-compiler
  getVariablesRef.current = getVariables

  return (
    <CodeMirror
      {...rest}
      data-disabled={disabled}
      aria-disabled={disabled}
      readOnly={disabled}
      role="textbox"
      extensions={React.useMemo(
        () => [
          themeExtension,
          // eslint-disable-next-line react-compiler/react-compiler
          autocompletion({
            closeOnBlur: false,
            override: [
              (context) => {
                const everything = context.matchBefore(/.*$/m)
                const word = context.matchBefore(/{[^}]*$/m) // stuff inside {}
                if (!word && (!everything || !context.explicit)) return null

                const [path, ..._formatting] =
                  word?.text.slice(1).split(',') ?? []
                const segments = path?.split('.') ?? []

                // remove last segment as it's incomplete
                segments.pop()
                // make sure there's at least one segment
                if (!segments[0]) {
                  segments[0] = ''
                }

                const variables = getVariablesRef
                  .current(segments, {
                    isExplicit: context.explicit,
                    isOutsideKeys: !word,
                  })
                  .map((v) => {
                    let label =
                      segments.filter((s) => s !== '').join('.') + '.' + v.label
                    if (label.startsWith('.')) {
                      label = label.slice(1)
                    }
                    if (!word) {
                      label = '{' + label + '}'
                    }

                    return {
                      ...v,
                      label,
                      displayLabel: v.displayLabel || v.label,
                      info: hideInfo ? undefined : v.info,
                    }
                  })

                return {
                  from: word ? word.from + 1 : (everything?.to ?? 0),
                  options: variables,
                }
              },
            ],
          }),
          // linter((view) => {
          //   const diagnostics: Diagnostic[] = []
          //   console.log(view)
          //   console.log(syntaxTree(view.state).cursor())
          //   syntaxTree(view.state)
          //     .cursor()
          //     .iterate((node) => {
          //       console.log('node', node)
          //       if (node.name == 'RegExp') {
          //         diagnostics.push({
          //           from: node.from,
          //           to: node.to,
          //           severity: 'warning',
          //           message: 'Regular expressions are FORBIDDEN',
          //           actions: [
          //             {
          //               name: 'Remove',
          //               apply(view, from, to) {
          //                 view.dispatch({ changes: { from, to } })
          //               },
          //             },
          //           ],
          //         })
          //       }
          //     })
          //   return diagnostics
          // }),
        ],
        [hideInfo]
      )}
    />
  )
}
